import { Link } from "react-router-dom";

/* Technical micro-label — mono, uppercase, wide tracking, blue tick */
export const TechLabel = ({ children, className = "" }) => (
    <p className={`label-tech flex items-center gap-2 ${className}`}>
        <span className="inline-block h-[7px] w-[7px] bg-[#1677FF]" aria-hidden="true" />
        <span>{children}</span>
    </p>
);

/* Section heading block: index / eyebrow / big display title / description */
export const SectionHeading = ({ index, eyebrow, title, description, className = "" }) => (
    <div className={className}>
        {eyebrow && (
            <TechLabel className="mb-5">
                {index ? `${index} / ` : ""}
                {eyebrow}
            </TechLabel>
        )}
        <h2 className="font-display text-[clamp(1.9rem,4.4vw,3.4rem)] font-bold uppercase leading-[1.04] tracking-tightest text-ink">
            {title}
        </h2>
        {description && (
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-mute md:text-lg">{description}</p>
        )}
    </div>
);

/* Demo-data tags — the distinction between preview and real data must be obvious */
export const PreviewTag = ({ children = "PROTOCOL PREVIEW", className = "" }) => (
    <span
        data-testid="preview-tag"
        className={`inline-flex items-center gap-2 border border-[#31D8FF]/25 bg-[#31D8FF]/[0.06] px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-[#31D8FF] ${className}`}
    >
        <span className="h-1.5 w-1.5 animate-blink rounded-full bg-[#31D8FF]" aria-hidden="true" />
        {children}
    </span>
);

/* Buttons — sharp, mono, institutional */
export const btnPrimary =
    "inline-flex items-center justify-center gap-3 bg-[#1677FF] px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-[#3D8BFF] hover:shadow-[0_0_32px_rgba(22,119,255,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#31D8FF] disabled:opacity-40";
export const btnGhost =
    "inline-flex items-center justify-center gap-3 border border-white/15 px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-[#1677FF] hover:bg-[#1677FF]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#31D8FF] disabled:opacity-40";

export const PrimaryLink = ({ to, children, testid, className = "", onClick }) => (
    <Link to={to} data-testid={testid} className={`${btnPrimary} ${className}`} onClick={onClick}>
        {children}
    </Link>
);

export const GhostLink = ({ to, children, testid, className = "", onClick }) => (
    <Link to={to} data-testid={testid} className={`${btnGhost} ${className}`} onClick={onClick}>
        {children}
    </Link>
);

/* Corner ticks — blueprint precision marks */
export const CornerTicks = () => (
    <>
        <span aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-2.5 w-2.5 border-l border-t border-[#1677FF]/50" />
        <span aria-hidden="true" className="pointer-events-none absolute right-0 top-0 h-2.5 w-2.5 border-r border-t border-[#1677FF]/50" />
        <span aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 h-2.5 w-2.5 border-b border-l border-[#1677FF]/50" />
        <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-2.5 w-2.5 border-b border-r border-[#1677FF]/50" />
    </>
);

/* Mono key/value row used across cards */
export const MetaRow = ({ k, v, accent = false }) => (
    <div className="flex items-baseline justify-between gap-4 border-t border-white/[0.06] py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute">{k}</span>
        <span className={`font-mono text-xs font-medium tracking-wider ${accent ? "text-[#31D8FF]" : "text-ink"}`}>{v}</span>
    </div>
);
